import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { homeOwnershipInfoService } from '../../../../services/homeOwnershipInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('HomeOwnershipData');

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const HomeOwnershipDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>();
  const { targetUserId, viewOnly } = route.params || {};
  const [homeOwnershipData, setHomeOwnershipData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeedLocation, setShowDeedLocation] = useState<{ [key: string]: boolean }>({});
  const [showTrustLocation, setShowTrustLocation] = useState<{ [key: string]: boolean }>({});
  const [showAccountNumber, setShowAccountNumber] = useState<{ [key: string]: boolean }>({});
  const [showPolicyNumber, setShowPolicyNumber] = useState<{ [key: string]: boolean }>({});
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadHomeOwnershipData();
  }, []);

  const loadHomeOwnershipData = async () => {
    try {
      setLoading(true);
      const data = await homeOwnershipInfoService.getAllHomeOwnershipInfo(targetUserId);

      logger.debug('Loaded home ownership data:', data);
      logger.debug('Number of records:', data?.length || 0);

      if (data && data.length > 0) {
        logger.debug('First record ID:', data[0].id);
        logger.debug('First record propertyType:', data[0].propertyType);

        // Load vault data for each home ownership record
        const updatedData = await Promise.all(
          data.map(async (homeOwnership) => {
            try {
              const vaultData = await homeOwnershipInfoService.getHomeOwnershipVaultData(homeOwnership.id);
              if (vaultData) {
                homeOwnership.username = vaultData.username;
                homeOwnership.password = vaultData.password;
                homeOwnership.trustLocation = vaultData.trustLocation;
                homeOwnership.deedLocation = vaultData.deedLocation;
              }
            } catch (vaultError) {
              logger.debug('Error loading vault data for home ownership:', homeOwnership.id, vaultError);
            }
            return homeOwnership;
          })
        );

        setHomeOwnershipData(updatedData);
      } else {
        setHomeOwnershipData([]);
      }
    } catch (error) {
      logger.error('Error loading home ownership data:', error);
      Alert.alert('Error', 'Failed to load home ownership information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '';
    if (typeof amount === 'number') {
      return `$${amount.toFixed(2)}`;
    }
    // Remove $ if already present and format
    const cleanAmount = amount.toString().replace('$', '');
    return `$${cleanAmount}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    // If it's already in DD/MM/YYYY format, return as is
    if (dateString.includes('/')) {
      return dateString;
    }
    // Otherwise assume it's a date string and try to format
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    } catch {
      return dateString;
    }
  };

  const handleEditHomeOwnership = (homeOwnership: any) => {
    // Route to correct input screen based on property type
    if (homeOwnership.propertyType === 'rent') {
      navigation.navigate('HomeRenterInput', {
        homeOwnershipId: homeOwnership.id,
        isEdit: true
      });
    } else {
      navigation.navigate('HomeOwnershipInput', {
        homeOwnershipId: homeOwnership.id,
        isEdit: true
      });
    }
  };

  const handleDeleteHomeOwnership = async (homeOwnership: any) => {
    logger.debug('Attempting to delete home ownership with ID:', homeOwnership.id);
    logger.debug('Full homeOwnership object:', homeOwnership);

    if (!homeOwnership.id) {
      Alert.alert('Error', 'Cannot delete: No ID found for this record');
      return;
    }

    Alert.alert('Delete', 'Are you sure you want to delete this home information?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await homeOwnershipInfoService.deleteHomeOwnershipInfo(homeOwnership.id);
            // Reload data after deletion
            await loadHomeOwnershipData();
            Alert.alert('Deleted', 'Home information deleted.');
          } catch (e) {
            logger.error('Delete home ownership info failed:', e);
            logger.error('Failed to delete ID:', homeOwnership.id);
            Alert.alert('Error', 'Failed to delete home information. The record may not exist in the database.');
          }
        }
      },
    ]);
  };

  const handleAddHomeOwnership = () => {
    navigation.navigate('HomeOwnershipIntro');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Home Ownership Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading home ownership information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!homeOwnershipData || homeOwnershipData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Home Ownership Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly
                ? "This user has not added living arrangements information yet."
                : "No home information found"}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddHomeOwnership}
              >
                <Text style={styles.addButtonText}>Add Home Information</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Home Ownership Information</Text>

      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {homeOwnershipData.map((homeOwnership, index) => {
            const isOwnership = homeOwnership.propertyType === 'own';
            const cardTitle = isOwnership ? 'Home Ownership' : 'Rental Property';

            return (
              <View key={homeOwnership.id || index} style={styles.homeCard}>
                <View style={styles.homeHeader}>
                  <View style={{flex: 1, flexShrink: 1}}>
                    <Text style={styles.homeName}>
                      {cardTitle} {index + 1}
                    </Text>
                    <Text style={styles.homeType}>
                      {isOwnership ? 'Owned Property' : 'Rental Property'}
                    </Text>
                  </View>
                  {!viewOnly && (
                    <View style={styles.cardButtons}>
                      <TouchableOpacity
                        style={styles.editHomeButton}
                        onPress={() => handleEditHomeOwnership(homeOwnership)}
                      >
                        <Text style={styles.editHomeButtonText}>Edit</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.deleteHomeButton}
                        onPress={() => handleDeleteHomeOwnership(homeOwnership)}
                      >
                        <Text style={styles.deleteHomeButtonText}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>

                {isOwnership ? (
                  <>
                    {homeOwnership.inTrust && (
                      <InfoItem label="In a Trust?" value={homeOwnership.inTrust} />
                    )}

                    {homeOwnership.deedLocation && (
                      <View style={styles.infoItem}>
                        <Text style={styles.label}>Deed Location 🔒</Text>
                        <View style={styles.valueRow}>
                          <Text style={styles.value}>
                            {showDeedLocation[homeOwnership.id] ? homeOwnership.deedLocation : '••••••••••'}
                          </Text>
                          <TouchableOpacity
                            onPress={() => setShowDeedLocation(prev => ({ ...prev, [homeOwnership.id]: !prev[homeOwnership.id] }))}
                            style={styles.eyeButton}
                          >
                            <Image
                              source={showDeedLocation[homeOwnership.id]
                                ? require('../../../../assets/icons/Eye.png')
                                : require('../../../../assets/icons/Eye-closed.png')}
                              style={styles.eyeIcon}
                            />
                          </TouchableOpacity>
                        </View>
                        <View style={styles.separator} />
                      </View>
                    )}

                    {homeOwnership.trustLocation && (
                      <View style={styles.infoItem}>
                        <Text style={styles.label}>Trust Location 🔒</Text>
                        <View style={styles.valueRow}>
                          <Text style={styles.value}>
                            {showTrustLocation[homeOwnership.id] ? homeOwnership.trustLocation : '••••••••••'}
                          </Text>
                          <TouchableOpacity
                            onPress={() => setShowTrustLocation(prev => ({ ...prev, [homeOwnership.id]: !prev[homeOwnership.id] }))}
                            style={styles.eyeButton}
                          >
                            <Image
                              source={showTrustLocation[homeOwnership.id]
                                ? require('../../../../assets/icons/Eye.png')
                                : require('../../../../assets/icons/Eye-closed.png')}
                              style={styles.eyeIcon}
                            />
                          </TouchableOpacity>
                        </View>
                        <View style={styles.separator} />
                      </View>
                    )}

                    <InfoItem label="Trust Attorney Name" value={homeOwnership.trustAttorneyName || ''} />
                    <InfoItem label="Practice Name" value={homeOwnership.trustAttorneyPracticeName || ''} />
                    <InfoItem label="Office Phone" value={formatPhoneNumber(homeOwnership.trustAttorneyOfficePhone) || ''} />
                    <InfoItem label="Cell Phone" value={formatPhoneNumber(homeOwnership.trustAttorneyCellPhone) || ''} />
                    <InfoItem label="Email" value={homeOwnership.trustAttorneyEmail || ''} />
                    <InfoItem label="Street Address" value={homeOwnership.trustAttorneyStreet || ''} />
                    <InfoItem label="City" value={homeOwnership.trustAttorneyCity || ''} />
                    <InfoItem label="State" value={homeOwnership.trustAttorneyState || ''} />
                    <InfoItem label="Zip Code" value={homeOwnership.trustAttorneyZip || ''} />

                    {homeOwnership.lenderName && (
                      <>
                        <InfoItem label="Lender Name" value={homeOwnership.lenderName} />
                        {homeOwnership.lenderAccountNumber && (
                          <View style={styles.infoItem}>
                            <Text style={styles.label}>Account Number 🔒</Text>
                            <View style={styles.valueRow}>
                              <Text style={styles.value}>
                                {showAccountNumber[homeOwnership.id] ? homeOwnership.lenderAccountNumber : '••••••••••'}
                              </Text>
                              <TouchableOpacity
                                onPress={() => setShowAccountNumber(prev => ({ ...prev, [homeOwnership.id]: !prev[homeOwnership.id] }))}
                                style={styles.eyeButton}
                              >
                                <Image
                                  source={showAccountNumber[homeOwnership.id]
                                    ? require('../../../../assets/icons/Eye.png')
                                    : require('../../../../assets/icons/Eye-closed.png')}
                                  style={styles.eyeIcon}
                                />
                              </TouchableOpacity>
                            </View>
                            <View style={styles.separator} />
                          </View>
                        )}
                        <InfoItem label="Lender Phone" value={formatPhoneNumber(homeOwnership.lenderPhone) || ''} />
                        <InfoItem label="Monthly Payment Amount" value={formatCurrency(homeOwnership.monthlyPaymentAmount) || ''} />
                        <InfoItem label="Payment Due Date" value={formatDate(homeOwnership.monthlyPaymentDate) || ''} />
                        <InfoItem label="Loan Termination Date" value={formatDate(homeOwnership.terminationDate) || ''} />
                        <InfoItem label="Lender Website" value={homeOwnership.lenderWebsite || ''} />
                        <InfoItem label="Lender Email" value={homeOwnership.mortgageEmail || ''} />

                        {homeOwnership.username && (
                          <View style={styles.infoItem}>
                            <Text style={styles.label}>Portal Username</Text>
                            <View style={styles.valueRow}>
                              <Text style={styles.value}>
                                {showUsername[homeOwnership.id] ? homeOwnership.username : '••••••••••'}
                              </Text>
                              <TouchableOpacity
                                onPress={() => setShowUsername(prev => ({ ...prev, [homeOwnership.id]: !prev[homeOwnership.id] }))}
                                style={styles.eyeButton}
                              >
                                <Image
                                  source={showUsername[homeOwnership.id]
                                    ? require('../../../../assets/icons/Eye.png')
                                    : require('../../../../assets/icons/Eye-closed.png')}
                                  style={styles.eyeIcon}
                                />
                              </TouchableOpacity>
                            </View>
                            <View style={styles.separator} />
                          </View>
                        )}

                        {homeOwnership.password && (
                          <View style={styles.infoItem}>
                            <Text style={styles.label}>Portal Password</Text>
                            <View style={styles.valueRow}>
                              <Text style={styles.value}>
                                {showPassword[homeOwnership.id] ? homeOwnership.password : '••••••••••'}
                              </Text>
                              <TouchableOpacity
                                onPress={() => setShowPassword(prev => ({ ...prev, [homeOwnership.id]: !prev[homeOwnership.id] }))}
                                style={styles.eyeButton}
                              >
                                <Image
                                  source={showPassword[homeOwnership.id]
                                    ? require('../../../../assets/icons/Eye.png')
                                    : require('../../../../assets/icons/Eye-closed.png')}
                                  style={styles.eyeIcon}
                                />
                              </TouchableOpacity>
                            </View>
                            <View style={styles.separator} />
                          </View>
                        )}
                      </>
                    )}

                    <InfoItem label="Special Instructions" value={homeOwnership.specialInstructions || ''} />
                  </>
                ) : (
                  <>
                    <InfoItem label="Landlord Name" value={homeOwnership.landlordName || ''} />
                    <InfoItem label="Landlord Phone" value={formatPhoneNumber(homeOwnership.landlordPhone) || ''} />
                    <InfoItem label="Landlord Email" value={homeOwnership.landlordEmail || ''} />
                    <InfoItem label="Property Management Company" value={homeOwnership.propertyManagementCompany || ''} />
                    <InfoItem label="Management Phone" value={formatPhoneNumber(homeOwnership.managementPhone) || ''} />
                    <InfoItem label="Management Email" value={homeOwnership.managementEmail || ''} />
                    <InfoItem label="Lease Start Date" value={formatDate(homeOwnership.leaseStartDate) || ''} />
                    <InfoItem label="Lease End Date" value={formatDate(homeOwnership.leaseEndDate) || ''} />
                    <InfoItem label="Monthly Rent Amount" value={formatCurrency(homeOwnership.monthlyRentAmount) || ''} />
                    <InfoItem label="Security Deposit" value={formatCurrency(homeOwnership.securityDepositAmount) || ''} />
                    <InfoItem label="Pet Deposit" value={formatCurrency(homeOwnership.petDepositAmount) || ''} />
                    <InfoItem label="Renters Insurance Provider" value={homeOwnership.rentersInsuranceProvider || ''} />

                    {homeOwnership.rentersInsurancePolicyNumber && (
                      <View style={styles.infoItem}>
                        <Text style={styles.label}>Policy Number 🔒</Text>
                        <View style={styles.valueRow}>
                          <Text style={styles.value}>
                            {showPolicyNumber[homeOwnership.id] ? homeOwnership.rentersInsurancePolicyNumber : '••••••••••'}
                          </Text>
                          <TouchableOpacity
                            onPress={() => setShowPolicyNumber(prev => ({ ...prev, [homeOwnership.id]: !prev[homeOwnership.id] }))}
                            style={styles.eyeButton}
                          >
                            <Image
                              source={showPolicyNumber[homeOwnership.id]
                                ? require('../../../../assets/icons/Eye.png')
                                : require('../../../../assets/icons/Eye-closed.png')}
                              style={styles.eyeIcon}
                            />
                          </TouchableOpacity>
                        </View>
                        <View style={styles.separator} />
                      </View>
                    )}

                    <InfoItem label="Special Instructions" value={homeOwnership.specialInstructions || ''} />
                  </>
                )}
              </View>
            );
          })}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherHomeButton}
                onPress={handleAddHomeOwnership}
              >
                <Text style={styles.addAnotherHomeButtonText}>Add Another Home</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('PropertyChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  homeCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  homeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  homeName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  homeType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editHomeButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editHomeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteHomeButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteHomeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherHomeButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherHomeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});